import { Box, Grid, Typography, Button } from '@mui/material';
import type { Asset } from 'types/asset.types';
import { PNG } from 'components/Asset/PNG';
import {
  assetTypeMappings,
  getAssetTypeLabelKey,
} from 'views/FleetOverview/helpers/asset.helpers';
import { getImageNameByMakeModelAndAsset } from 'utils/helpers/mapAssetImage';
import { IgnitionStatusIndicator } from 'components/StatusIndicator';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

export const AssetDetails = ({
  enableDarkTheme,
  asset,
  hierarchyInfo,
  latestTagData,
}: {
  enableDarkTheme: boolean;
  asset: Asset;
  hierarchyInfo: any;
  latestTagData: any;
}) => {
  const { t } = useTranslation();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const imageName = getImageNameByMakeModelAndAsset(
    asset.make || '',
    asset.model || '',
    asset.assetType,
    'Small'
  );

  const statusText = asset.isActive ? 'Operational' : 'Out Of Service';
  const statusColor = asset.isActive ? '#4e9967' : '#7a4c4c';

  return (
    <Grid item lg={3} md={5} sm={12} xs={12}>
      <Box
        sx={{
          padding: '20px',
          backgroundColor: palette.boxBackground,
          borderRadius: '5px',
          border: `1px solid rgba(255, 255, 255, 0.15)`,
          minHeight: '170px',
        }}
      >
        <Typography
          sx={{
            fontSize: '14px',
            color: '#ffffff',
            marginBottom: '16px',
          }}
        >
          {t('asset_profile')}
        </Typography>

        <Box
          display="flex"
          alignItems="center"
          gap={3}
          sx={{
            flexDirection: { xs: 'column', sm: 'row' },
          }}
        >
          {/* Circular Image */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              minWidth: '120px',
              minHeight: '120px',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                // backgroundColor: '#EEEEEE',
                backgroundColor: enableDarkTheme ? '#5c6a7c' : '#EEEEEE',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              <Box
                component={PNG}
                name={imageName}
                alt={asset.bumperNumber}
                sx={{
                  position: 'absolute',
                  width: '140px',
                  height: '140px',
                  objectFit: 'contain',
                  zIndex: 2,
                }}
              />
            </Box>
          </Box>

          {/* Asset Information */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              flex: 1,
            }}
          >
            {/* Asset ID */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <IgnitionStatusIndicator
                latestTagData={latestTagData}
                asset={asset}
                size={10}
                marginRight="5px"
              />
              <Typography
                sx={{
                  fontSize: '20px',
                  fontWeight: 'bold',
                  color: '#ffffff',
                  lineHeight: 1,
                }}
              >
                {asset.bumperNumber}
              </Typography>
            </Box>

            <Typography
              sx={{
                fontSize: '12px',
                color: '#ffffff',
                fontWeight: 400,
              }}
            >
              {asset.make || 'CAT'} • {asset.model || '980M'}
            </Typography>

            {/* Asset Type */}
            <Typography
              sx={{
                fontSize: '12px',
                color: '#ffffff',
                fontWeight: 400,
              }}
            >
              {t(
                getAssetTypeLabelKey(
                  assetTypeMappings[asset.assetType] || 'Loader'
                )
              )}
            </Typography>

            {/* Status Button */}
            <Box sx={{ marginTop: '4px' }}>
              <Button
                sx={{
                  backgroundColor: statusColor,
                  color: '#ffffff',
                  borderRadius: '20px',
                  padding: '4px 16px',
                  fontSize: '12px',
                  fontWeight: 400,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: statusColor,
                    opacity: 0.9,
                  },
                }}
              >
                {statusText}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Grid>
  );
};
